import * as React from 'react'
import { FC, useCallback, useState } from 'react'
import Divider from '@mui/material/Divider'
import Popover from '@mui/material/Popover'
import { getStyles } from './useStyles'
import AddIcon from '@mui/icons-material/Add'
import Button, { buttonClasses } from '@mui/material/Button'
import {
  permissionDenied,
  useCheckPermissions,
} from '@user/store/authorization/permissions'
import { LightTooltip } from '@sales-center/components'
import useTheme from '@mui/material/styles/useTheme'
import Box from '@mui/material/Box'

interface Props {
  handleLinkClick: (url: string) => void
}

export const AddButton: FC<Props> = (props) => {
  const { handleLinkClick } = props

  const [canAddLead, canCreateOpp] = useCheckPermissions(
    'leads_add',
    'opportunities_create',
  )
  const disableAddButton = React.useMemo(
    () => !canAddLead && !canCreateOpp,
    [canAddLead, canCreateOpp],
  )

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)
  const id = open ? 'add-popover' : undefined

  const onOpen = useCallback(
    (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget),
    [],
  )

  const onClose = useCallback(() => setAnchorEl(null), [])

  const linkClickHandler = useCallback(
    (url: string) => {
      onClose()
      handleLinkClick(url)
    },
    [onClose, handleLinkClick],
  )

  const onAddLead = useCallback(
    () => linkClickHandler('/leads/addLead'),
    [linkClickHandler],
  )

  const onAddOpp = useCallback(
    () => linkClickHandler('/opportunities/addOpportunity'),
    [linkClickHandler],
  )

  const theme = useTheme()
  const styles = getStyles(theme)

  return (
    <>
      <LightTooltip title={disableAddButton ? permissionDenied : ''}>
        <Box component="span">
          <Button
            sx={styles.addButton}
            disableRipple
            variant={open ? 'outlined' : 'contained'}
            data-test-id="leadOppAddBtn"
            onClick={onOpen}
            disabled={disableAddButton}
          >
            <AddIcon color="inherit" />
          </Button>
        </Box>
      </LightTooltip>
      <Popover
        id={id}
        open={open}
        onClose={onClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: { onMouseLeave: onClose, elevation: 3, sx: styles.content },
        }}
        sx={{
          ...styles.root,
        }}
      >
        <span style={{ ...styles.header, ...styles.text }}>RECORDS</span>

        <Divider />

        <div style={{ display: 'flex' }}>
          <LightTooltip title={!canAddLead ? permissionDenied : ''}>
            <Box component="span">
              <Button
                sx={{
                  [`&.${buttonClasses.root}`]: {
                    ...styles.button,
                    ...styles.label,
                  },
                  [`&.${buttonClasses.text}`]: { ...styles.text },
                }}
                onClick={onAddLead}
                disabled={!canAddLead}
                data-test-id="addLeadBtn"
              >
                <img alt="Add Lead" src="static-assets/icons/addLead.svg" />
                <div>Add Lead</div>
                <div style={{ fontFamily: 'Source Sans Pro' }}>
                  (New Customer)
                </div>
              </Button>
            </Box>
          </LightTooltip>

          <LightTooltip title={!canCreateOpp ? permissionDenied : ''}>
            <Box component="span">
              <Button
                sx={{
                  [`&.${buttonClasses.root}`]: {
                    ...styles.button,
                    ...styles.label,
                  },
                  [`&.${buttonClasses.text}`]: { ...styles.text },
                }}
                onClick={onAddOpp}
                disabled={!canCreateOpp}
                data-test-id="addOppBtn"
              >
                <img
                  alt="Create Opportunity"
                  src="static-assets/icons/createOpportunity.svg"
                />
                <div>Create Opportunity</div>
                <div style={{ fontFamily: 'Source Sans Pro' }}>
                  (Existing Customer)
                </div>
              </Button>
            </Box>
          </LightTooltip>
        </div>
      </Popover>
    </>
  )
}
AddButton.displayName = 'AddButton'
